import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Settings as SettingsIcon } from 'lucide-react';
import {
  Avatar,
  avatarLetter,
  Button,
  buttonVariants,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@eo/shared';

import { useAppSelector } from '../app/store';
import { logout } from '../auth/logout';

type Props = {
  collapsed: boolean;
};

export function UserMenu({ collapsed }: Props) {
  const me = useAppSelector((s) => s.staff.me);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);

  if (!me) return null;

  const signOut = async () => {
    setPending(true);
    try {
      await logout();
    } finally {
      // logout() normally navigates away; if it fails the button recovers.
      setPending(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Account menu for ${me.name}`}
          className={cn(
            'flex h-12 cursor-pointer items-center gap-2 rounded-md transition-colors hover:bg-sidebar-neutral-hover',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
            collapsed ? 'w-8 justify-center px-0' : 'w-full pl-2 pr-4',
          )}
        >
          <Avatar
            size="sm"
            letter={avatarLetter(me.name, me.email)}
            imageUrl={me.avatarUrl}
            alt=""
            className="size-6 text-xs"
          />
          {!collapsed && (
            <span className="flex min-w-0 flex-col text-left">
              <span className="truncate text-sm text-sidebar-foreground">{me.name}</span>
              <span className="truncate text-xs text-sidebar-muted">Profile &amp; Settings</span>
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="top" className="w-64 rounded-12 p-1">
        <div className="flex flex-col px-2 py-2">
          <span className="truncate text-sm font-medium text-text-primary">{me.name}</span>
          <span className="truncate text-xs text-text-tertiary" title={me.email}>
            {me.email}
          </span>
        </div>
        <div className="my-1 h-px bg-border-faint" />
        <Link
          to="/settings"
          onClick={() => setOpen(false)}
          className={cn(buttonVariants({ variant: 'ghost' }), 'w-full justify-start font-[450]')}
        >
          <SettingsIcon className="size-4" />
          Settings
        </Link>
        <Button
          type="button"
          variant="ghost"
          onClick={() => void signOut()}
          disabled={pending}
          className="w-full justify-start font-[450] text-destructive hover:bg-button-destructive-hover hover:text-destructive"
        >
          <LogOut className="size-4" />
          {pending ? 'Signing out…' : 'Sign out'}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
